// Ported from EnterpriseTemplates react-project-template (Release 1.3.1), PMS slice 4b.
// ============================================================================
// ORG ADMIN SERVICE
// ============================================================================
// Entities, business units and financial years of the tenant through
// /api/v1/org/*. Every create and update is written through to the Tenant Hub
// organisation master first and projected locally in one transaction; a write
// the master refuses writes nothing locally (400 ORGANISATION_MASTER_REJECTED)
// and a master that cannot be reached answers 503.

import { get, post, patch } from '../client/axiosClient';
import { organisationEndpoints } from '../endpoints/organisation.endpoints';
import type { ApiResponse, BackendPagedResult, ID, PaginatedResponse } from '../types';
import type {
  OrganisationKind, OrganisationListParams, OrganisationLookups,
  OrganisationSyncResult, OrganisationVerifyResult,
} from '../types/organisation.types';

class OrganisationService {
  /** Paged, searchable list of one kind (server-side paging; pageSize ≤ 200). */
  async list<T>(kind: OrganisationKind, params?: OrganisationListParams): Promise<PaginatedResponse<T>> {
    const endpoint = params
      ? organisationEndpoints.listWithQuery(kind, params as Record<string, any>)
      : organisationEndpoints.list(kind);
    const response = await get<ApiResponse<BackendPagedResult<T>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged?.items || []),
      pagination: {
        currentPage: paged?.page ?? 1,
        pageSize: paged?.pageSize ?? (params?.pageSize ?? 25),
        totalPages: paged?.totalPages ?? 0,
        totalItems: paged?.totalCount ?? 0,
        hasNextPage: paged?.hasNext ?? false,
        hasPreviousPage: paged?.hasPrevious ?? false,
      },
    };
  }

  async create<T, R = unknown>(kind: OrganisationKind, command: R): Promise<ApiResponse<T>> {
    const response = await post<ApiResponse<T>>(organisationEndpoints.list(kind), command);
    return response.data;
  }

  async update<T, R = unknown>(kind: OrganisationKind, id: ID, command: R): Promise<ApiResponse<T>> {
    const response = await patch<ApiResponse<T>>(organisationEndpoints.byId(kind, id), command);
    return response.data;
  }

  async getLookups(): Promise<ApiResponse<OrganisationLookups>> {
    const response = await get<ApiResponse<OrganisationLookups>>(organisationEndpoints.lookups());
    return response.data;
  }

  /** SUPER_ADMIN or IAM_ADMIN. */
  async sync(force = false): Promise<ApiResponse<OrganisationSyncResult>> {
    const response = await post<ApiResponse<OrganisationSyncResult>>(organisationEndpoints.sync(force), {});
    return response.data;
  }

  /** SUPER_ADMIN or IAM_ADMIN. */
  async verify(): Promise<ApiResponse<OrganisationVerifyResult>> {
    const response = await get<ApiResponse<OrganisationVerifyResult>>(organisationEndpoints.verify());
    return response.data;
  }
}

export const organisationService = new OrganisationService();
export default organisationService;
